import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  ClipboardList,
  Users,
  Scale,
  Briefcase,
  FileText,
  Check,
  AlertCircle,
  Lightbulb,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/App";

// Sanitize HTML content to prevent XSS attacks
const sanitizeHtml = (html) => {
  if (!html) return "";
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
};

const iconMap = {
  Calendar,
  ClipboardList,
  Users,
  Scale,
  Briefcase,
  FileText,
  BarChart3,
};

export default function DataGuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGuide = async () => {
      try {
        const res = await api.get(`/data-guides/${slug}`);
        setGuide(res.data);
      } catch (error) {
        // Error fetching data guide
      } finally {
        setLoading(false);
      }
    };
    fetchGuide();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Guide Not Found</h1>
          <Link to="/data">
            <Button>Back to Data Guides</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[guide.icon] || BarChart3;

  return (
    <div data-testid="data-guide-detail-page">
      {/* Header */}
      <section 
        className="py-16 text-white"
        style={{ backgroundColor: guide.color || "#3B82F6" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/data" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6"
            data-testid="back-to-guides"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Data Guides
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-6"
          >
            <div className="w-16 h-16 bg-white/20 rounded-sm flex items-center justify-center flex-shrink-0">
              <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-mono text-white/70 uppercase mb-2">
                {guide.domain} Data
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="guide-title">
                {guide.title}
              </h1>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* What to Pull */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <ClipboardList className="w-6 h-6" />
                  What Data to Pull
                </h2>
                <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="what-to-pull">
                  <ul className="space-y-3">
                    {guide.what_to_pull?.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* How to Interpret */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Lightbulb className="w-6 h-6" />
                  How to Interpret
                </h2>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-6" data-testid="interpretation">
                  <div 
                    className="text-slate-700 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(guide.how_to_interpret) }}
                  />
                </div>
              </div>

              {/* Common Patterns */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6" />
                  Common Patterns & Implications
                </h2>
                <div className="space-y-4" data-testid="common-patterns">
                  {guide.common_patterns?.map((pattern, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white border border-slate-200 rounded-sm p-6"
                    >
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                          style={{ backgroundColor: guide.color }}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 mb-2">
                            {pattern.pattern}
                          </h3>
                          <p className="text-slate-600">
                            <span className="font-medium">Implication: </span>
                            {pattern.implication}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* First 90 Day Actions */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6" />
                  First 90-Day Actions
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-6" data-testid="actions">
                  <p className="text-sm text-blue-600 mb-4 font-medium">
                    Grounded in the "coherence" stance for PPS leadership
                  </p>
                  <div className="space-y-3">
                    {guide.first_90_day_actions?.map((action, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="text-slate-700">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Stats */}
                <div className="bg-white border border-slate-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Guide Overview</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Domain</span>
                      <span className="font-medium capitalize">{guide.domain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Data Points</span>
                      <span className="font-mono font-medium">{guide.what_to_pull?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Patterns</span>
                      <span className="font-mono font-medium">{guide.common_patterns?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Actions</span>
                      <span className="font-mono font-medium">{guide.first_90_day_actions?.length || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Related */}
                <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Related Resources</h3>
                  <div className="space-y-3">
                    <Link to="/data" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="all-guides-btn">
                        All Data Guides
                      </Button>
                    </Link>
                    <Link to="/protocols" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="protocols-btn">
                        Related Protocols
                      </Button>
                    </Link>
                    <Link to="/framework" className="block">
                      <Button variant="outline" className="w-full justify-start" data-testid="framework-btn">
                        PPS Framework
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Help */}
                <div 
                  className="border p-6 rounded-sm text-white"
                  style={{ backgroundColor: guide.color }}
                >
                  <h3 className="font-bold mb-2">Need Help?</h3>
                  <p className="text-sm text-white/80 mb-4">
                    Want support analyzing your district's data in this domain?
                  </p>
                  <Link to="/about#contact">
                    <Button className="w-full bg-white text-slate-900 hover:bg-slate-100" data-testid="get-help-btn">
                      Get in Touch
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
